import React from 'react';
import { Link } from 'react-router-dom';
import { useMarket } from '../../context/MarketContext';
import { getProductPrice, getProductsForMarket } from '../../data/products';
import { productTheme } from '../../utils/productTheme';
import ProductIcon from '../../components/products/ProductIcon';

const Products: React.FC = () => {
  const { market } = useMarket();
  const marketProducts = getProductsForMarket(market);

  return (
    <div className="pt-24">
      <section className="pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-3">
            Our Products
          </span>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3">
            Software solutions that <span className="gradient-text">work for you</span>
          </h1>
          <p className="text-slate-700 dark:text-dark-300 max-w-2xl mx-auto">
            Powerful, cloud-based products designed to simplify your business operations.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-8 ${marketProducts.length > 1 ? 'md:grid-cols-2' : 'max-w-2xl mx-auto'}`}>
            {marketProducts.map((product) => {
              const theme = productTheme(product.color);
              return (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className={`group card p-8 hover:scale-[1.02] transition-all duration-300 ${theme.borderHover} hover:shadow-xl`}
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl flex-shrink-0 ${theme.iconTile} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <ProductIcon icon={product.icon} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                          {product.name}
                        </h2>
                      </div>
                      <p className={`text-sm font-medium ${theme.text}`}>
                        {product.tagline}
                      </p>
                    </div>
                  </div>

                  <p className="text-slate-700 dark:text-dark-300 mb-6 line-clamp-2">
                    {product.description}
                  </p>

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-dark-400 uppercase tracking-wider mb-3">
                      Key Features
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {product.features.slice(0, 4).map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-700 dark:text-dark-300">
                          <svg className={`w-4 h-4 flex-shrink-0 ${theme.textStrong}`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="truncate">{feature.title}</span>
                        </div>
                      ))}
                    </div>
                    {product.features.length > 4 && (
                      <p className={`text-sm mt-2 font-medium ${theme.text}`}>
                        +{product.features.length - 4} more features
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-dark-800">
                    <span className="text-slate-900 dark:text-white font-semibold">
                      {getProductPrice(product, market)}
                    </span>
                    <div className={`flex items-center gap-2 font-semibold ${theme.text} group-hover:gap-4 transition-all duration-300`}>
                      Learn more
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
                Ready to get started?
              </h2>
              <p className="text-slate-700 dark:text-dark-300 text-lg mb-8 max-w-xl mx-auto">
                Try our products free or contact us to learn more about how they can transform your business.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/pricing" className="btn-primary">
                  View Pricing
                </Link>
                <Link to="/contact" className="btn-secondary">
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
